import Heading from "Components/Heading";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { MdDateRange } from "react-icons/md";
import { BiSearch, BiExport } from "react-icons/bi";
import StationCodesAPIs from "../../APIs/station-code";
import { Formik } from "formik";
import * as Yup from "yup";
import Footer from "Components/Footer";
import TicketAPIs from "../../APIs/ticket";
import SampleDataTable from "Components/DataTables";
import { formatDate } from "Helper/Converters";
import { toast } from "react-toastify";
import moment from "moment";
import { exportFileToExcel } from "Utils/utils";
import { useSelector } from "react-redux";

const MONTH_OPTIONS = [
  { value: "", label: "Select" },
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

const YEAR_OPTIONS = [
  { value: "", label: "Select" },
  { value: 2020, label: "2020" },
  { value: 2021, label: "2021" },
  { value: 2022, label: "2022" },
  { value: 2023, label: "2023" },
  { value: 2024, label: "2024" },
  { value: 2025, label: "2025" },
  { value: 2026, label: "2026" },
  { value: 2027, label: "2027" },
  { value: 2028, label: "2028" },
  { value: 2029, label: "2029" },
  { value: 2030, label: "2030" },
];

const getMonthRangeDates = (monthIndex, year) => {
  const base = moment({ year, month: monthIndex, day: 1 });
  return {
    fromDate: base.clone().startOf("month").toDate(),
    toDate: base.clone().endOf("month").toDate(),
  };
};

const DEFAULT_MONTH = moment().month();
const DEFAULT_YEAR = moment().year();

const defaultMonthOption =
  MONTH_OPTIONS.find((m) => m.value === DEFAULT_MONTH) || MONTH_OPTIONS[0];
const defaultYearOption =
  YEAR_OPTIONS.find((y) => y.value === DEFAULT_YEAR) || YEAR_OPTIONS[0];

const defaultRange = getMonthRangeDates(
  defaultMonthOption.value,
  defaultYearOption.value
);

const INITIAL_VALUES = {
  stationCodeIds: [],
  // UI removed dateType, but we always send enteredDate
  // month: defaultMonthOption,
  // year: defaultYearOption,
  // fromDate: defaultRange.fromDate,
  // toDate: defaultRange.toDate,
  month: null,
  year: null,
  fromDate: null,
  toDate: null,
  nofDays: 5,
};

const ValidationSchema = Yup.object().shape({
  month: Yup.object()
    .nullable()
    .required("Month is required")
    .shape({
      value: Yup.number().required("Month is required"),
    }),
  year: Yup.object()
    .nullable()
    .required("Year is required")
    .shape({
      value: Yup.number().required("Year is required"),
    }),
  nofDays: Yup.number().min(1, "Business Days must be atleast 1"),
});

const NonComplaintReportwithMonthAndYear = () => {
  const [stationCodes, setStationCodes] = useState([]);
  const [isCompliant, setIsCompliant] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const [searchedMasterData, setSearchedMasterData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user } = useSelector((state) => state.auth);

  // ✅ react-select: full width + fixed height + no wrapping + dropdown not tiny
  const customStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "44px",
      height: "44px",
      borderRadius: "6px",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.25)" : "none",
      borderColor: state.isFocused ? "#86b7fe" : provided.borderColor,
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "44px",
      padding: "0 12px",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      whiteSpace: "nowrap",
      overflow: "hidden",
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "44px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menu: (provided) => ({
      ...provided,
      minWidth: "260px",
      zIndex: 9999,
    }),
    option: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
    }),
  };

  const allowedStations = useMemo(() => {
    const fromRedux = user?.allowedStations;
    if (Array.isArray(fromRedux) && fromRedux.length) return fromRedux;
    return [];
  }, [user]);

  const getAllStationCodes = async () => {
    const res = await StationCodesAPIs.getStationCode();

    if (res.data?.success) {
      const all = (res.data.data || [])
        ?.filter((item) => item.isFilterable)
        .sort((a, b) => a.name.localeCompare(b.name));

      const filtered = allowedStations?.length
        ? all.filter((sc) => allowedStations.includes(sc.name))
        : [];

      setStationCodes(filtered);
    }
  };

  useEffect(() => {
    getAllStationCodes();
  }, [allowedStations?.join(",")]);

   // Formik initialValues that auto-select ALL station codes once loaded
  const formInitialValues = useMemo(() => {
    return {
      ...INITIAL_VALUES,
      stationCodeIds: stationCodes?.length ? stationCodes.map((s) => s.id) : [],
    };
  }, [stationCodes]);

  const cleanAddress = (address) => {
    return address
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(", ");
  };

  const handleSubmit = async (values) => {

  //  Require at least 1 station selected
  const selectedStations = values.stationCodeIds || [];
    if (selectedStations.length === 0) {
      toast.error("Please select a station code to fetch the record.");
      return;
  }

  const selectedMonth = values?.month?.value; // 0-11
  const selectedYear = values?.year?.value;

  const currentMonth = moment().month();
  const currentYear = moment().year();

  //  Block current month + current year
  if (selectedMonth === currentMonth && selectedYear === currentYear) {
    toast.error("Cannot fetch the record of the current month.");
    return; // 🚫 do not call API
  }

    const payload = {
      stationCodeIds: values.stationCodeIds,
      dateType: "enteredDate", //  always this
      fromDate: values.fromDate ? moment(values.fromDate).format("YYYY-MM-DD") : null,
      toDate: values.toDate ? moment(values.toDate).format("YYYY-MM-DD") : null,
      nofDays: values.nofDays.toString(),
    };

    setLoading(true);
    const search = await TicketAPIs.nonCompliantReport(payload);

    if (search.data?.success) {
      const arr =
        search.data.data.map((item) => ({
          ...item,
          digaddress: cleanAddress(item.digaddress),
        })) || [];

      setSearchedMasterData(arr);

      if (!isCompliant) setSearchedData(arr.filter((item) => item.nonCompliantReason != ""));
      else setSearchedData(arr);
    }

    setLoading(false);
  };

  const columns = [
    {
      accessor: "ticket_no",
      Header: "Ticket No",
      Cell: ({ row }) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            const url = `/dashboard/ticket-detail/${row.original.ticket_no}`;
            const windowName = `popUpWindow_${row.original.ticket_no}`;
            const windowSize = "width=800,height=600";
            window.open(url, windowName, windowSize);
          }}
          href={`/dashboard/ticket-detail/${row.original.ticket_no}`}
          rel="noopener noreferrer"
          className={`badge ${
            row.original.ticketType == "EMERGENCY" ||
            row.original.priority == "EMERGENCY"
              ? "emergency"
              : row.original.ticketType == "MULTIPLE" ||
                row.original.priority == "MULTIPLE"
              ? "multiple"
              : row.original.ticketType == "PRIORITY" ||
                row.original.priority == "PRIORITY"
              ? "priority"
              : row.original.ticketType == "PROJECT WORK" ||
                row.original.priority == "PROJECT WORK"
              ? "projectWork"
              : row.original.ticketType == "STANDARD" ||
                row.original.priority == "STANDARD"
              ? "standard"
              : "default"
          }`}
        >
          {row.original.ticket_no}
        </a>
      ),
    },
    { Header: "Address", Cell: ({ row }) => <p>{row.original.digaddress}</p> },
    { accessor: "contractorname", Header: "Contractor" },
    {
      Header: "Entered Date",
      Cell: ({ row }) => (
        <p>{row.original.entereddate ? formatDate(row.original.entereddate) : ""}</p>
      ),
    },
    {
      Header: "Compliance Date",
      Cell: ({ row }) => (
        <p>
          {row.original.originalcompliancedate
            ? formatDate(row.original.originalcompliancedate)
            : ""}
        </p>
      ),
    },
    {
      Header: "Renegotiated Date",
      Cell: ({ row }) => (
        <p>{row.original.renegotiateddate ? formatDate(row.original.renegotiateddate) : ""}</p>
      ),
    },
    {
      Header: "Completed Date",
      Cell: ({ row }) => (
        <p>{row.original.completeddate ? formatDate(row.original.completeddate) : ""}</p>
      ),
    },
    {
      Header: "Pre Completed Date",
      Cell: ({ row }) => (
        <p>{row.original.precompleteddate ? formatDate(row.original.precompleteddate) : ""}</p>
      ),
    },
    {
      Header: "Renegotiated Attempt Date",
      Cell: ({ row }) => (
        <p>
          {row.original.renegotiatedattempteddate
            ? formatDate(row.original.renegotiatedattempteddate)
            : ""}
        </p>
      ),
    },
    {
      Header: "Non Compliant Reason",
      Cell: ({ row }) => <p>{row.original.nonCompliantReason}</p>,
    },
  ];

  const formatDateForExcel = () => {
    let formattedData = [
      [
        { v: "Ticket No.", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Address", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Contractor", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Entered Date", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Compliance Date", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Renegotiated Date", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Completed Date", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Renegotiated Attempt Date", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
        { v: "Non Compliant Reason", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
      ],
    ];

    searchedData.forEach((item) => {
      formattedData.push([
        { v: item.ticket_no },
        { v: item.digaddress },
        { v: item.contractorname },
        { v: item.entereddate ? formatDate(item.entereddate) : "" },
        {
          v: item.complianceDate
            ? item.hasticketrescheduledremark == 1
              ? formatDate(item.originalcompliancedate)
              : formatDate(item.complianceDate)
            : "",
        },
        { v: item.renegotiateddate ? formatDate(item.renegotiateddate) : "" },
        { v: item.completeddate ? formatDate(item.completeddate) : "" },
        {
          v: item.renegotiatedattempteddate
            ? formatDate(item.renegotiatedattempteddate)
            : "",
        },
        { v: item.nonCompliantReason },
      ]);
    });

    return formattedData;
  };

  const exportToExcel = () => {
    if (!(searchedData.length > 0)) {
      toast.error("No data found yet!");
      return;
    }
    const data = formatDateForExcel();
    const fileName = "Non_Compliance_Report";
    exportFileToExcel(data, fileName);
  };

  const renderPercentage = useMemo(() => {
    if (!searchedMasterData.length) return "0.00";
    return Number(
      (searchedMasterData.filter((item) => item.nonCompliantReason === "").length /
        searchedMasterData.length) *
        100
    ).toFixed(2);
  }, [searchedMasterData]);

  useEffect(() => {
    if (searchedMasterData.length > 0) {
      if (isCompliant) setSearchedData(JSON.parse(JSON.stringify(searchedMasterData)));
      else setSearchedData(searchedMasterData.filter((item) => item.nonCompliantReason !== ""));
    }
  }, [isCompliant, searchedMasterData]);

  return (
    <>
      <section>
        <Heading text={"Non Compliance Report"} />

        <Card>
          <Formik
            // initialValues={INITIAL_VALUES}
            initialValues={formInitialValues}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              setFieldValue,
              setFieldTouched,
            }) => {
              const updateRange = (monthOption, yearOption) => {
                if (!monthOption?.value && monthOption?.value !== 0) return;
                if (!yearOption?.value) return;

                const range = getMonthRangeDates(monthOption.value, yearOption.value);
                setFieldValue("fromDate", range.fromDate);
                setFieldValue("toDate", range.toDate);
              };

              const SelectWithIcon = ({ label, name, options, value, onChange, error }) => (
                <Form.Group className="form-group">
                  <Form.Label>{label}</Form.Label>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                    {/* ✅ Select takes full width (fixes tiny placeholder like Jun/202) */}
                    <div style={{ flex: 1, minWidth: 260 }}>
                      <Select
                        name={name}
                        options={options}
                        styles={customStyles}
                        value={value}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        onChange={onChange}
                        onBlur={() => setFieldTouched(name, true, true)}
                      />
                    </div>

                    {/* ✅ Fixed icon box (no pushing/select shrinking) */}
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#dff0ff",
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    >
                      <MdDateRange />
                    </div>
                  </div>

                  {error ? <span className="error-msg">{error}</span> : null}
                </Form.Group>
              );

              return (
                <Form className="dashboard-form" onSubmit={handleSubmit}>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="form-group">
                        <Form.Label>Station Codes</Form.Label>
                        <div className="checkboxHolder">
                          {stationCodes.map((item, index) => {
                            const selected = values.stationCodeIds || [];
                            const isChecked = selected.includes(item.id);

                            return (
                              <Form.Check
                                key={item.id}
                                type="checkbox"
                                label={item.name}
                                name={`stationCodes-${index}`}
                                checked={isChecked}
                                onChange={() => {
                                  if (isChecked) {
                                    setFieldValue(
                                      "stationCodeIds",
                                      selected.filter((id) => id != item.id)
                                    );
                                  } else {
                                    setFieldValue("stationCodeIds", [...selected, item.id]);
                                  }
                                }}
                              />
                            );
                          })}
                        </div>
                      </Form.Group>
                    </Col>

                    {/* ✅ Month + Year (proper width + height) */}
                    <Col md={6} sm={12} className="column">
                      <SelectWithIcon
                        label="Month"
                        name="month"
                        options={MONTH_OPTIONS}
                        value={values.month}
                        onChange={(val) => {
                          setFieldValue("month", val);
                          updateRange(val, values.year);
                        }}
                        error={touched.month && errors.month?.value ? errors.month?.value : ""}
                      />
                    </Col>

                    <Col md={6} sm={12} className="column">
                      <SelectWithIcon
                        label="Year"
                        name="year"
                        options={YEAR_OPTIONS}
                        value={values.year}
                        onChange={(val) => {
                          setFieldValue("year", val);
                          updateRange(values.month, val);
                        }}
                        error={touched.year && errors.year?.value ? errors.year?.value : ""}
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Group className="form-group">
                        <Form.Label>
                          Compliance days set to{" "}
                          <input
                            type="number"
                            name="nofDays"
                            value={values.nofDays}
                            min={1}
                            onChange={handleChange}
                            onBlur={() => setFieldTouched("nofDays", true, true)}
                            style={{ width: 30, textAlign: "center" }}
                            className="number-input-without-arrows"
                          />{" "}
                          business days after entered data
                        </Form.Label>

                        <div className="checkboxHolder">
                          <Form.Check
                            type="checkbox"
                            className="include-check"
                            label="Include Complaints"
                            checked={isCompliant}
                            onChange={(e) => setIsCompliant(e.target.checked)}
                          />
                        </div>

                        <div className="mt-2">
                          <small>
                            {/* Range:{" "}
                            <strong>{moment(values.fromDate).format("YYYY-MM-DD")}</strong> to{" "}
                            <strong>{moment(values.toDate).format("YYYY-MM-DD")}</strong> */}
                             Range:{" "}
                            {values.fromDate && values.toDate ? (
                              <>
                                <strong>{moment(values.fromDate).format("YYYY-MM-DD")}</strong> to{" "}
                                <strong>{moment(values.toDate).format("YYYY-MM-DD")}</strong>
                              </>
                            ) : (
                              <strong>Select month & year to see range</strong>
                            )}
                          </small>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={12} className="d-flex justify-content-between mb-4">
                      <Button onClick={exportToExcel}>
                        <BiExport /> Export
                      </Button>
                      <Button style={{ minHeight: "44px" }} type="submit">
                        <BiSearch /> {loading ? "Loading..." : "Search"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>

          <>
            {searchedMasterData[0] && (
              <strong className="mb-4">
                Estimated Compliance:{" "}
                <span style={{ backgroundColor: "#ec9706" }} className="px-1">
                  {renderPercentage}% Compliant
                </span>
              </strong>
            )}
            <SampleDataTable columns={columns} data={searchedData} />
          </>
          <Footer />
        </Card>
      </section>
    </>
  );
};

export default NonComplaintReportwithMonthAndYear;